import type { ThemeName, ThemeTokens } from "../types";

export interface PresetPair {
  light: ThemeTokens;
  dark: ThemeTokens;
}

const FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/**
 * Note that `secondaryBg` defaults to the accent in every preset: "Accept all" and
 * "Reject all" render from the same style, so they are provably equivalent in size,
 * weight and colour as the DSK guidance demands. Overriding it is possible but the
 * dev-mode check will complain if the two stop matching.
 */
export const PRESETS: Record<ThemeName, PresetPair> = {
  porcelain: {
    light: {
      bg: "#ffffff",
      surface: "#faf6f0",
      text: "#211d1a",
      textMuted: "#6f6659",
      // Deeper than Tymbo's display persimmon (#e8542f). That brand value gives only
      // 3.48:1 against its on-accent cream, which fails WCAG 1.4.3 for 14px button text;
      // #c8401f reads as the same colour and clears 4.5:1.
      accent: "#c8401f",
      accentSoft: "#fbe3db",
      onAccent: "#fff8f3",
      secondaryBg: "#c8401f",
      secondaryText: "#fff8f3",
      border: "#e5dcce",
      radius: "20px",
      radiusSm: "12px",
      font: FONT,
      shadow: "0 12px 32px rgba(33, 29, 26, 0.06)",
      overlay: "rgba(33, 29, 26, 0.45)",
    },
    dark: {
      bg: "#211d19",
      surface: "#2b2620",
      text: "#f3ede5",
      textMuted: "#a89c8c",
      accent: "#ff6b44",
      accentSoft: "#3d251e",
      // Dark ink on the bright accent rather than cream: 6.09:1 instead of 2.69:1.
      onAccent: "#2a1610",
      secondaryBg: "#ff6b44",
      secondaryText: "#2a1610",
      border: "#38322a",
      radius: "20px",
      radiusSm: "12px",
      font: FONT,
      shadow: "0 12px 32px rgba(0, 0, 0, 0.4)",
      overlay: "rgba(10, 8, 7, 0.6)",
    },
  },
  midnight: {
    light: {
      bg: "#1b1d21",
      surface: "#24272c",
      text: "#f2f4f7",
      textMuted: "#a2a8b3",
      accent: "#7c8cf8",
      accentSoft: "#2a2f45",
      onAccent: "#0f1116",
      secondaryBg: "#7c8cf8",
      secondaryText: "#0f1116",
      border: "#343840",
      radius: "16px",
      radiusSm: "10px",
      font: FONT,
      shadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
      overlay: "rgba(8, 9, 11, 0.6)",
    },
    dark: {
      bg: "#131519",
      surface: "#1c1f24",
      text: "#f2f4f7",
      textMuted: "#9aa1ad",
      accent: "#8f9dff",
      accentSoft: "#242a40",
      onAccent: "#0d0f13",
      secondaryBg: "#8f9dff",
      secondaryText: "#0d0f13",
      border: "#2b2f37",
      radius: "16px",
      radiusSm: "10px",
      font: FONT,
      shadow: "0 12px 32px rgba(0, 0, 0, 0.55)",
      overlay: "rgba(5, 6, 8, 0.66)",
    },
  },
  mono: {
    light: {
      bg: "#ffffff",
      surface: "#f5f5f5",
      text: "#141414",
      textMuted: "#5c5c5c",
      accent: "#141414",
      accentSoft: "#ececec",
      onAccent: "#ffffff",
      secondaryBg: "#141414",
      secondaryText: "#ffffff",
      border: "#dcdcdc",
      radius: "8px",
      radiusSm: "6px",
      font: FONT,
      shadow: "0 12px 32px rgba(0, 0, 0, 0.06)",
      overlay: "rgba(20, 20, 20, 0.45)",
    },
    dark: {
      bg: "#161616",
      surface: "#1f1f1f",
      text: "#f4f4f4",
      textMuted: "#a6a6a6",
      accent: "#f4f4f4",
      accentSoft: "#2c2c2c",
      onAccent: "#141414",
      secondaryBg: "#f4f4f4",
      secondaryText: "#141414",
      border: "#303030",
      radius: "8px",
      radiusSm: "6px",
      font: FONT,
      shadow: "0 12px 32px rgba(0, 0, 0, 0.5)",
      overlay: "rgba(0, 0, 0, 0.6)",
    },
  },
  sky: {
    light: {
      bg: "#ffffff",
      surface: "#f4f8fd",
      text: "#0f172a",
      textMuted: "#5b6b84",
      accent: "#2563eb",
      accentSoft: "#dfe9fd",
      onAccent: "#ffffff",
      secondaryBg: "#2563eb",
      secondaryText: "#ffffff",
      border: "#dbe4f0",
      radius: "14px",
      radiusSm: "9px",
      font: FONT,
      shadow: "0 12px 32px rgba(15, 23, 42, 0.06)",
      overlay: "rgba(15, 23, 42, 0.45)",
    },
    dark: {
      bg: "#111a2b",
      surface: "#182339",
      text: "#e8eefb",
      textMuted: "#93a4c0",
      accent: "#5b8cf7",
      accentSoft: "#1d2c47",
      onAccent: "#0a1120",
      secondaryBg: "#5b8cf7",
      secondaryText: "#0a1120",
      border: "#26334c",
      radius: "14px",
      radiusSm: "9px",
      font: FONT,
      shadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
      overlay: "rgba(5, 10, 20, 0.6)",
    },
  },
};
